import { STATUS_ENUM } from 'constants/report';

export const QA_STATUS_FILTERS = [
  'needsQa',
  'inProgress',
  'approved',
  'rejected',
  'failed',
  'all',
] as const;

export type QaStatusFilter = (typeof QA_STATUS_FILTERS)[number];

export const DEFAULT_QA_STATUS_FILTER: QaStatusFilter = 'needsQa';

/** Subset of PostGraphile's ReportJobFilter used by the QA table */
export type ReportJobFilter = Record<string, unknown>;

const STATUS_FILTERS: Record<QaStatusFilter, ReportJobFilter | null> = {
  needsQa: {
    reportStatus: { equalTo: STATUS_ENUM.SUCCESS },
    passedQa: { isNull: true },
  },
  inProgress: {
    reportStatus: { notIn: [STATUS_ENUM.SUCCESS, STATUS_ENUM.FAILED] },
  },
  approved: { passedQa: { equalTo: true } },
  rejected: { passedQa: { equalTo: false } },
  failed: { reportStatus: { equalTo: STATUS_ENUM.FAILED } },
  all: null,
};

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isQaStatusFilter = (value: unknown): value is QaStatusFilter =>
  typeof value === 'string' &&
  (QA_STATUS_FILTERS as readonly string[]).includes(value);

/**
 * Server-side search across the columns shown in the table. A full UUID
 * matches the report or job id exactly.
 */
export const buildSearchFilter = (term: string): ReportJobFilter | null => {
  const trimmed = term.trim();
  if (!trimmed) return null;

  if (UUID_REGEX.test(trimmed)) {
    return {
      or: [
        { reportId: { equalTo: trimmed } },
        { jobId: { equalTo: trimmed } },
      ],
    };
  }

  const contains = { includesInsensitive: trimmed };
  return {
    or: [
      { projectName: contains },
      { targetGroupName: contains },
      { requestedThrough: contains },
      { clientName: contains },
      { reportType: contains },
      { producerByWorkspaceId: { name: contains } },
    ],
  };
};

const combineFilters = (
  ...filters: (ReportJobFilter | null)[]
): ReportJobFilter | null => {
  const present = filters.filter(Boolean);
  if (present.length === 0) return null;
  if (present.length === 1) return present[0];
  return { and: present };
};

export const buildReportsQaFilter = (
  status: QaStatusFilter,
  searchTerm: string,
): ReportJobFilter | null =>
  combineFilters(STATUS_FILTERS[status], buildSearchFilter(searchTerm));

export const buildReportsQaCountFilters = (
  searchTerm: string,
): Record<QaStatusFilter, ReportJobFilter | null> =>
  QA_STATUS_FILTERS.reduce(
    (acc, status) => ({
      ...acc,
      [status]: buildReportsQaFilter(status, searchTerm),
    }),
    {} as Record<QaStatusFilter, ReportJobFilter | null>,
  );

/** Table column id -> PostGraphile ReportJobsOrderBy base name */
export const SORTABLE_COLUMNS: Record<string, string> = {
  name: 'PROJECT_NAME',
  reportType: 'REPORT_TYPE',
  targetGroup: 'TARGET_GROUP_NAME',
  user: 'REQUESTED_THROUGH',
  submittedOn: 'STARTED_AT',
  status: 'REPORT_STATUS',
};

export const DEFAULT_SORT_COLUMN = 'submittedOn';

export const buildOrderBy = (
  column: string,
  direction: 'asc' | 'desc',
): string[] => {
  const base = SORTABLE_COLUMNS[column] ?? SORTABLE_COLUMNS[DEFAULT_SORT_COLUMN];
  // ID is a stable tie-breaker so paging never skips or repeats rows
  return [`${base}_${direction.toUpperCase()}`, 'ID_DESC'];
};
